import {
  ExceptionReporter,
  setExceptionReporter,
} from '@pogokid/log'
import type { ErrorEvent } from '@sentry/browser'
import { getPublicConfig } from '~/config.ts'

/** Deploy stages that report errors, matching soniq-rest. */
const REPORTING_STAGES = ['production', 'canary']

/**
 * Set this `localStorage` key to `off` to stop this browser sending
 * error reports. This is how someone objects to error reporting,
 * and what the switch on the account Privacy page changes.
 */
export const ERROR_REPORTS_OPT_OUT_KEY = 'soniq_error_reports'

/**
 * The reporter, once the SDK has loaded and started. Kept as a
 * promise so the SDK only starts once, however often reporting is
 * switched back on.
 */
let bugsinkReporter: Promise<ExceptionReporter> | undefined

/** True when this browser has turned error reports off. */
export function hasOptedOutOfErrorReports(): boolean {
  try {
    return (
      localStorage.getItem(ERROR_REPORTS_OPT_OUT_KEY) === 'off'
    )
  } catch {
    // Storage can be blocked (private windows, strict settings).
    // We can't read a choice, so report as normal.
    return false
  }
}

/**
 * Turn error reports off or on for this browser. Takes effect
 * straight away, and is remembered for later visits.
 *
 * If storage is blocked the choice can't be saved, but it still
 * applies until the page is reloaded.
 */
export function setOptedOutOfErrorReports(optedOut: boolean) {
  try {
    if (optedOut) {
      localStorage.setItem(ERROR_REPORTS_OPT_OUT_KEY, 'off')
    } else {
      localStorage.removeItem(ERROR_REPORTS_OPT_OUT_KEY)
    }
  } catch {
    // See above: the choice lasts for this visit only
  }

  if (optedOut) {
    // Nothing is captured automatically, so removing the reporter
    // is enough to stop every report.
    setExceptionReporter(undefined)
  } else {
    void enableErrorReporting()
  }
}

/**
 * Pages whose URL itself grants access, so the secret part is
 * replaced before a report leaves the browser. Anyone holding the
 * URL could open the contact portal, pay the invoice or accept the
 * invitation.
 */
const SECRET_PATHS: [RegExp, string][] = [
  [/^\/portal\/[^/]+/, '/portal/:token'],
  [/^\/pay\/[^/]+/, '/pay/:link'],
  [/^\/invite\/[^/]+/, '/invite/:id'],
]

/**
 * Remove anything secret from a page URL before it is reported.
 *
 * The query string and fragment are dropped entirely: they carry
 * sign-in codes, payment provider return values and invitation
 * ids, none of which are needed to diagnose an error. Secret path
 * segments are replaced with a placeholder (see `SECRET_PATHS`).
 */
export function maskUrl(url: string): string {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    // Not a URL we can pick apart, so report none of it
    return '[unparseable url]'
  }
  const path = SECRET_PATHS.reduce(
    (path, [pattern, placeholder]) =>
      path.replace(pattern, placeholder),
    parsed.pathname
  )
  return `${parsed.origin}${path}`
}

/** Mask the page URL and referrer on an outgoing report. */
export function maskEventUrls(event: ErrorEvent): ErrorEvent {
  const request = event.request
  if (!request) return event

  const headers = { ...request.headers }
  if (headers.Referer) headers.Referer = maskUrl(headers.Referer)

  return {
    ...event,
    request: {
      ...request,
      url: request.url ? maskUrl(request.url) : request.url,
      query_string: undefined,
      headers,
    },
  }
}

/**
 * Send errors passed to `captureException` on to our self-hosted
 * Bugsink, which speaks the Sentry wire protocol.
 *
 * Only production and canary report, and only when the build was
 * given a `VITE_SENTRY_DSN`. Everywhere else errors stay in the
 * console. A browser can opt out (see `ERROR_REPORTS_OPT_OUT_KEY`).
 *
 * Nothing is captured automatically: uncaught errors, console
 * output and network requests are not collected. An event is sent
 * only when the app calls `captureException`, and its page URL is
 * masked first (see `maskUrl`).
 *
 * The SDK is loaded on demand, so builds that don't report never
 * download it.
 */
export async function enableErrorReporting() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const { environment } = getPublicConfig()
  if (
    !dsn ||
    !REPORTING_STAGES.includes(environment) ||
    hasOptedOutOfErrorReports()
  ) {
    return
  }

  bugsinkReporter ??= startBugsink(dsn, environment)
  const reporter = await bugsinkReporter

  // The person may have opted out while the SDK was loading
  if (hasOptedOutOfErrorReports()) return
  setExceptionReporter(reporter)
}

async function startBugsink(
  dsn: string,
  environment: string
): Promise<ExceptionReporter> {
  const Sentry = await import('@sentry/browser')
  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_SONIQ_VERSION || undefined,
    // Leave out the default integrations, which would report
    // uncaught errors and record console and network activity.
    // Keep only the ones that shape a reported error:
    // linked errors follow `error.cause`, dedupe drops repeats,
    // and http context adds the page URL and browser.
    defaultIntegrations: false,
    integrations: [
      Sentry.linkedErrorsIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.httpContextIntegration(),
    ],
    beforeSend: maskEventUrls,
  })

  return (error, context) => {
    const reportable = toReportableError(error)
    const extra = { ...reportable.data, ...context?.data }
    Sentry.captureException(reportable.error, {
      extra: Object.keys(extra).length ? extra : undefined,
    })
  }
}

/**
 * Turn an error-like object into a real `Error` before it is sent.
 *
 * Some libraries hand over a plain object with a `name` and
 * `message` instead of an `Error`. react-oidc-context does this
 * for sign in and token renewal failures. The SDK marks such an
 * object as "synthetic", and Bugsink then ignores its name and
 * message, so the issue has no title.
 *
 * The object's `innerError` becomes the `cause`, so the original
 * error is reported too. Its other plain fields (e.g.
 * react-oidc-context's `source`) are returned as extra data.
 */
export function toReportableError(error: unknown): {
  error: unknown
  data: Record<string, unknown>
} {
  if (
    error instanceof Error ||
    typeof error !== 'object' ||
    error === null ||
    typeof (error as { message?: unknown }).message !== 'string'
  ) {
    return { error, data: {} }
  }

  const { name, message, stack, innerError, ...rest } =
    error as Record<string, unknown>
  const converted = new Error(message as string, {
    cause: innerError,
  })
  if (typeof name === 'string') converted.name = name
  if (typeof stack === 'string' && stack) converted.stack = stack

  const data = Object.fromEntries(
    Object.entries(rest).filter(([, value]) =>
      ['string', 'number', 'boolean'].includes(typeof value)
    )
  )
  return { error: converted, data }
}
